// Every blog post, in the order shown on /blog. BlogPage renders the cards
// from this list, and CyberpunkPostLayout builds each post's Article
// structured data from it (image and dates).
//
// datePublished: dates marked ESTIMATE are "published no later than" bounds,
// taken from the first Wayback Machine capture or, where the Internet Archive
// has none, from the commit that added the post. Replace them with the real
// publish dates if you have them.

export type BlogPostType = "Infographic" | "Feature" | "Strategy";

export type BlogPost = {
  path: string;
  title: string;
  excerpt: string;
  thumb: string;
  alt: string;
  type: BlogPostType;
  meta: string[];
  datePublished: string;
  dateModified?: string;
};

export const BLOG_POSTS: BlogPost[] = [
  {
    path: "/blog/saaspocalypse",
    title: "The SaaSpocalypse: Why Small Businesses Are Actually Winning",
    excerpt:
      "Salesforce down 26%, Intuit down 34%. The SaaS giants are bleeding—but the narrative is wrong. For enterprises, it's consolidation. For small business? Liberation.",
    thumb: "/SaaSpocalypse.webp",
    alt: "The SaaSpocalypse small business opportunity thumbnail",
    type: "Feature",
    meta: ["Feature", "Feb 13, 2026"],
    // date on the post
    datePublished: "2026-02-13",
  },
  {
    path: "/blog/ai-arms-race-opus-vs-codex",
    title: "The AI Arms Race Heats Up",
    excerpt:
      "Claude Opus 4.6 vs GPT-5.3 Codex isn't about which is better—it's about which is better for what. A practitioner's take on multi-model orchestration.",
    thumb: "/AIArmsRace.webp",
    alt: "AI Arms Race Opus vs Codex thumbnail",
    type: "Feature",
    meta: ["Feature", "Feb 6, 2026"],
    // date on the post
    datePublished: "2026-02-06",
  },
  {
    path: "/blog/ai-agents-mainstream",
    title: "The Week AI Agents Went Mainstream",
    excerpt:
      "OpenClaw hit 100K GitHub stars. Mac Minis are sold out. But AI agent capabilities are incredible while the UI is still terrible. A practitioner's take.",
    thumb: "/AIAgentsMainstream.webp",
    alt: "AI Agents Mainstream thumbnail",
    type: "Infographic",
    meta: ["Infographic", "Feb 3, 2026"],
    // date on the post
    datePublished: "2026-02-03",
  },
  {
    path: "/blog/ai-automation-real-estate",
    title: "Areas for AI Automation in Real Estate",
    excerpt:
      "Identify high-impact automation opportunities in lead qualification, market analysis, client communication, and transaction management. Focus on repetitive data processing and administrative tasks.",
    thumb: "/AIAutomationRealEstate.webp",
    alt: "Areas for AI Automation in Real Estate thumbnail",
    type: "Strategy",
    meta: ["Strategy", "New"],
    // ESTIMATE: no Wayback capture; first appears in the repo 2025-11-21
    datePublished: "2025-11-21",
  },
  {
    path: "/blog/mcp-contractors",
    title: "What is MCP? (For Contractors & Light Manufacturing)",
    excerpt:
      "Model Context Protocol enables AI agents to securely connect with business systems like CRMs and ERPs. Direct API access with built-in security controls and no per-automation fees.",
    thumb: "/MCPContractors.webp",
    alt: "MCP for Contractors and Manufacturing thumbnail",
    type: "Strategy",
    meta: ["Strategy", "New"],
    // ESTIMATE: first Wayback capture 2025-09-08
    datePublished: "2025-09-08",
  },
  {
    path: "/blog/genai-paradox",
    title: "The GenAI Paradox",
    excerpt:
      "A Strategic Analysis of Top AI News This Week. Unprecedented breakthroughs collide with profound challenges in the rapidly evolving AI landscape.",
    thumb: "/GenAIParadox.webp",
    alt: "The GenAI Paradox infographic thumbnail",
    type: "Infographic",
    meta: ["Infographic", "Aug 16-22, 2025"],
    // date previously set on the post
    datePublished: "2025-08-16",
  },
  {
    path: "/blog/ai-week-review",
    title: "AI Week in Review",
    excerpt:
      "August 8-15, 2025: A Landscape of Rapid Innovation and Rising Stakes. Key developments in AI models, funding, regulation, and the growing trust deficit.",
    thumb: "/AIWeekReview.webp",
    alt: "AI Week in Review infographic thumbnail",
    type: "Infographic",
    meta: ["Infographic", "Aug 8–15, 2025"],
    // date previously set on the post
    datePublished: "2025-08-22",
  },
  {
    path: "/blog/ai-changed-course",
    title: "The Week AI Changed Course",
    excerpt:
      "The industry pivots from a simple performance race to a multi-front war for agentic supremacy, open-source dominance, and elite talent.",
    thumb: "/AIChangedCourse.webp",
    alt: "The Week AI Changed Course thumbnail",
    type: "Infographic",
    meta: ["Infographic", "Aug 1–7, 2025"],
    // ESTIMATE: covers Aug 1-7, 2025; first Wayback capture 2025-09-08
    datePublished: "2025-09-08",
  },
  {
    path: "/blog/ai-paradox",
    title: "The AI Paradox for Small Business",
    excerpt:
      "AI adoption is surging, yet many projects fail. Success requires strategy, not just tools.",
    thumb: "/AIParadox.webp",
    alt: "AI Paradox for Small Business thumbnail",
    type: "Infographic",
    meta: ["Infographic", "New"],
    // ESTIMATE: no Wayback capture; first appears in the repo 2025-11-21
    datePublished: "2025-11-21",
  },
  {
    path: "/blog/custom-automation-solutions",
    title: "Custom Automation Solutions for Small Business",
    excerpt:
      "Why 'fitted' beats 'generic' every time—and how to decide when to go custom.",
    thumb: "/CustomAutomation.webp",
    alt: "Custom Automation for Small Business thumbnail",
    type: "Strategy",
    meta: ["Strategy", "New"],
    // ESTIMATE: first Wayback capture 2025-09-08
    datePublished: "2025-09-08",
  },
  {
    path: "/blog/ai-workflow-automation",
    title: "AI Workflow Automation: A Practical Guide for Small Business",
    excerpt:
      "Learn what AI workflow automation is, how it differs from rules-based automation, which workflows to automate, what tools to use, and how to get started.",
    thumb: "/AIWorkflowAutomation.webp",
    alt: "AI Workflow Automation article thumbnail",
    type: "Strategy",
    meta: ["Strategy", "New"],
    // ESTIMATE: first Wayback capture 2025-09-08; rebuilt as a guide 2026-09-14 (#90)
    datePublished: "2025-09-08",
    dateModified: "2026-09-14",
  },
  {
    path: "/blog/automated-business-intelligence",
    title: "Automated Business Intelligence Solutions: From Data Chaos to Decision Advantage",
    excerpt:
      "Build a unified, real-time, decision-ready BI stack that turns data into competitive advantage.",
    thumb: "/AutomatedBusinessIntelligence.webp",
    alt: "Automated Business Intelligence article thumbnail",
    type: "Strategy",
    meta: ["Strategy", "New"],
    // ESTIMATE: first Wayback capture 2025-09-08
    datePublished: "2025-09-08",
  },
  {
    path: "/blog/no-code-automation-platforms",
    title: "No-Code Automation Platforms: When They Work, When They Don't, and What Comes Next",
    excerpt:
      "When no-code works, when it stalls, and how to choose between Zapier, Make.com, n8n, and AI builders like Plumb—plus a framework for going hybrid.",
    thumb: "/NoCodeAutomationPlatforms.webp",
    alt: "No-code automation platforms article thumbnail",
    type: "Strategy",
    meta: ["Strategy", "New"],
    // ESTIMATE: first Wayback capture 2025-09-08
    datePublished: "2025-09-08",
  },
  {
    path: "/blog/process-mining-automation",
    title: "Process Mining and Automation: Discovering What's Really Happening Before You Automate It",
    excerpt:
      "Evidence beats assumptions. Use process mining to see real workflows, quantify bottlenecks, and build automation that actually works.",
    thumb: "/ProcessMiningAutomation.webp",
    alt: "Process mining article thumbnail",
    type: "Strategy",
    meta: ["Strategy", "New"],
    // ESTIMATE: first Wayback capture 2025-09-08
    datePublished: "2025-09-08",
  },
  {
    path: "/blog/enterprise-automation-strategy",
    title: "Enterprise Automation Strategy: Scaling Beyond Small Business Solutions",
    excerpt:
      "Small business tools crack at enterprise scale. Build architecture, governance, and change management for transformation, not just efficiency.",
    thumb: "/EnterpriseAutomationStrategy.webp",
    alt: "Enterprise automation strategy article thumbnail",
    type: "Strategy",
    meta: ["Strategy", "New"],
    // ESTIMATE: first Wayback capture 2025-09-08
    datePublished: "2025-09-08",
  },
];

export const findBlogPost = (postPath: string): BlogPost | undefined =>
  BLOG_POSTS.find((post) => post.path === postPath);
